/**
 * DemoWelcomeBanner — the one-line personalized greeting pinned at the top of
 * the app shell for prospect (demo) sessions. Demo-gated by the parent
 * (DemoWelcomeChrome in DemoChrome.tsx); the text arrives display-ready from
 * GET /me (`me.demo.welcome` — the API already rendered {first_name}).
 *
 * Dismiss persists per browser + demo via localStorage
 * `chai-demo-welcome-seen:<subdomain>` (same key pattern as the tour), so a
 * prospect who closed it never sees it again on that machine while a
 * colleague on another laptop still does.
 *
 * LOOK: DARK, and badged, like every other piece of demo chrome
 * (./chromeTheme.ts). This band sits INSIDE the app shell rather than floating
 * over it, which is exactly why it needs the treatment most: a light brand-wash
 * strip at the top of a light app reads as a product banner the prospect should
 * act on, and it is not — it is the demo talking.
 */

import { XIcon } from "lucide-react";
import { useState } from "react";

import { DEMO_BADGE, demoChromeVars } from "./chromeTheme";

const seenKey = (subdomain: string, userKey: string) =>
  `chai-demo-welcome-seen:${subdomain || "demo"}:${userKey || "anon"}`;

export function DemoWelcomeBanner({
  message,
  subdomain,
  userKey = "",
}: {
  message: string;
  subdomain: string;
  userKey?: string;
}) {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(seenKey(subdomain, userKey)) !== null;
    } catch {
      return false; // storage blocked (private mode) — show, just don't persist
    }
  });
  if (dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(seenKey(subdomain, userKey), new Date().toISOString());
    } catch {
      /* storage blocked — dismissal still holds for this page's lifetime */
    }
  };

  return (
    <div
      // The narrator's band: dark ground, hairline underneath, brand accent
      // reserved for the badge and the rule. 150ms fade on appear; the
      // motion-safe variants keep it static under prefers-reduced-motion.
      className="flex w-full shrink-0 items-center gap-2.5 border-b py-1.5 pr-2 motion-safe:animate-in motion-safe:fade-in"
      style={{
        ...demoChromeVars,
        background: "var(--demo-surface)",
        color: "var(--demo-text)",
        borderColor: "var(--demo-border)",
        paddingLeft: "max(0.75rem, var(--titlebar-pad-l))",
      }}
    >
      <span className={`${DEMO_BADGE} shrink-0`}>Demo guide</span>
      <p className="min-w-0 flex-1 text-[13px] leading-snug">{message}</p>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss welcome message"
        className="flex size-6 shrink-0 items-center justify-center rounded-md text-[var(--demo-muted)] transition-colors hover:text-[var(--demo-text)] focus-visible:text-[var(--demo-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--demo-ring)]"
      >
        <XIcon className="size-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}
